import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box, Checkbox, FormControlLabel, IconButton, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EventDetails } from '../../../backendsdk';
import { TrackedButton as Button, TrackedDialog as Dialog } from '../../../components/TrackedComponents';
import { SetAlert } from '../../../hooks/alert';
import useApi from '../../../hooks/api';
import useProfile from '../../../hooks/profile';
import {
    VIDEO_CAMERA_ORDER,
    VideoStreamType,
    detectStreamCountWithScale,
    geussStream,
    streamLocation,
} from '../../../utils/ImageDimensions';
import { RTLDirectionContext } from '../../Layout';
import { OE_ROLES } from '../Users/UsersComponent';
import SingleStreamNoOverlay from './EventVideoImages/1-stream-no-overlay.svg';
import SingleStreamOverlay from './EventVideoImages/1-stream-overlay.svg';
import DSNoOverlay from './EventVideoImages/DS-no-overlay.svg';
import DSOverlay from './EventVideoImages/DS-overlay.svg';
import { getVideoStreams } from './EventVideoStreams';

export interface DownloadEventVideoModalComponentProps {
    eventDetails?: EventDetails;
    eventId: number;
    onClose: () => void;
    isInCabinPrimary: boolean;
    setAlert: SetAlert;
    numberOfStreams: number;
    artifact: string;
}

const filenameForEventDownload = (
    eventId: number,
    url: string,
    eventTimestamp?: number,
    licensePlate?: string,
): string => {
    let ext = 'mp4';
    try {
        const name = new URL(url).pathname.split('/').pop();
        const match = name?.match(/\.(mp4|webm|mov)$/i);
        if (match) ext = match[1].toLowerCase();
    } catch {
        // ignore invalid URL
    }
    const timePart = eventTimestamp != null ? `_${dayjs.unix(eventTimestamp).format('YYYY-MM-DDTHH-mm-ss')}` : '';
    const platePart =
        licensePlate != null && licensePlate.trim() !== ''
            ? `${licensePlate.replace(/[/\\:*?"<>|]/g, '_').trim()}_`
            : '';
    return `OpenEyes_${platePart}event_${eventId}${timePart}.${ext}`;
};

const DownloadEventVideoModalComponent: React.FC<DownloadEventVideoModalComponentProps> = (
    props: DownloadEventVideoModalComponentProps,
) => {
    const [quickDownloadLoading, setQuickDownloadLoading] = useState<boolean>(false);
    const [addOverlay, setAddOverlay] = useState<boolean>(false);
    const [unblur, setUnblur] = useState<boolean>(false);
    const [enableStreamSelection, setEnableStreamSelection] = useState<boolean>(false);
    const [availableStreams, setAvailableStreams] = useState<Set<VideoStreamType>>(new Set());
    const [selectedStreams, setSelectedStreams] = useState<Set<VideoStreamType>>(new Set());
    const [streamLocations, setStreamLocations] = useState<Record<VideoStreamType, streamLocation>>(
        {} as Record<VideoStreamType, streamLocation>,
    );
    const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const { api } = useApi();
    const { t } = useTranslation();
    const { profile } = useProfile();
    const theme = useTheme();
    const isRTL = useContext(RTLDirectionContext);

    const canUnblur =
        OE_ROLES.includes(profile.role) &&
        (props.eventDetails?.tags || []).map((tag) => tag.toLocaleLowerCase()).includes('maskreversibleblur');

    // Load available streams from eventDetails metadata
    useEffect(() => {
        if (props.eventDetails) {
            getVideoStreams(props.eventDetails, (fileContent: string) => {
                const header = fileContent
                    .split('\n')
                    .filter((line) => line.trim() !== '' && line.includes('streams'))[0];
                if (header) {
                    try {
                        const metadata = JSON.parse(header.replaceAll(';', ''));
                        const streams = metadata.streams || {};
                        const streamNames = Object.keys(streams) as VideoStreamType[];
                        const availableSet = new Set(streamNames);
                        setAvailableStreams(availableSet);
                        // Default: all streams selected
                        setSelectedStreams(new Set(availableSet));
                        // Store stream locations from metadata
                        const locations: Record<VideoStreamType, streamLocation> = {} as Record<
                            VideoStreamType,
                            streamLocation
                        >;
                        streamNames.forEach((stream) => {
                            if (streams[stream] && Array.isArray(streams[stream]) && streams[stream].length === 4) {
                                locations[stream] = streams[stream] as streamLocation;
                            }
                        });
                        setStreamLocations(locations);
                    } catch (e) {
                        // If metadata parsing fails, use numberOfStreams to guess
                        const streamCount = props.numberOfStreams || 2;
                        const guessedStreams = VIDEO_CAMERA_ORDER.slice(0, streamCount);
                        const availableSet = new Set(guessedStreams);
                        setAvailableStreams(availableSet);
                        setSelectedStreams(new Set(availableSet));
                        // Stream locations will be calculated from image dimensions
                    }
                } else {
                    // Fallback: use numberOfStreams to guess
                    const streamCount = props.numberOfStreams || 2;
                    const guessedStreams = VIDEO_CAMERA_ORDER.slice(0, streamCount);
                    const availableSet = new Set(guessedStreams);
                    setAvailableStreams(availableSet);
                    setSelectedStreams(new Set(availableSet));
                    // Stream locations will be calculated from image dimensions
                }
            });
        } else {
            // Fallback: use numberOfStreams to guess
            const streamCount = props.numberOfStreams || 2;
            const guessedStreams = VIDEO_CAMERA_ORDER.slice(0, streamCount);
            const availableSet = new Set(guessedStreams);
            setAvailableStreams(availableSet);
            setSelectedStreams(new Set(availableSet));
            // Stream locations will be calculated from image dimensions
        }
    }, [props.eventDetails, props.numberOfStreams]);

    // Calculate stream locations from image dimensions if not available from metadata
    useEffect(() => {
        if (imageDimensions && Object.keys(streamLocations).length === 0 && availableStreams.size > 0) {
            const locations: Record<VideoStreamType, streamLocation> = {} as Record<VideoStreamType, streamLocation>;
            Array.from(availableStreams).forEach((stream) => {
                locations[stream] = geussStream(stream, imageDimensions.width, imageDimensions.height);
            });
            setStreamLocations(locations);
        }
    }, [imageDimensions, availableStreams, streamLocations]);

    // Handle image load to get dimensions
    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    };

    // Toggle stream selection
    const toggleStreamSelection = (stream: VideoStreamType) => {
        setSelectedStreams((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(stream)) {
                newSet.delete(stream);
            } else {
                newSet.add(stream);
            }
            return newSet;
        });
    };

    // Get relative position for stream overlay based on image display size
    const getStreamOverlayStyle = (stream: VideoStreamType): React.CSSProperties | undefined => {
        if (!imageRef.current || !streamLocations[stream] || !enableStreamSelection) {
            return undefined;
        }
        let [left, top, width, height] = streamLocations[stream];
        const img = imageRef.current;
        const imgRect = img.getBoundingClientRect();

        // Detect which scale was used for stream detection (1 or 2)
        const streamCountScale1 = detectStreamCountWithScale(img.naturalWidth, img.naturalHeight, 1);
        const streamCountScale2 = detectStreamCountWithScale(img.naturalWidth, img.naturalHeight, 2);
        const imageScale = streamCountScale1 > 0 ? 1 : streamCountScale2 > 0 ? 2 : 1;

        // When scale 2 is detected, locations from metadata are at full scale (scale 1),
        // but calculated locations from geussStream are already divided by 2.
        // We need to normalize: if locations look like they're at full scale for a scale 2 image,
        // divide them by 2. Calculated locations from geussStream are already correct.
        if (imageScale === 2) {
            // Check if locations appear to be at full scale (not already divided)
            // For scale 2 images, calculated locations should be <= half the image dimensions.
            // If width or height is > 60% of image dimensions, they're likely at full scale.
            const threshold = 0.6;
            if (width > img.naturalWidth * threshold || height > img.naturalHeight * threshold) {
                // Locations are at full scale, need to divide by 2
                left = left / 2;
                top = top / 2;
                width = width / 2;
                height = height / 2;
            }
        }

        // Calculate display scale (how much the image is scaled for display)
        const scaleX = imgRect.width / img.naturalWidth;
        const scaleY = imgRect.height / img.naturalHeight;

        const isSelected = selectedStreams.has(stream);

        return {
            position: 'absolute',
            insetInlineStart: isRTL ? undefined : `${left * scaleX}px`,
            insetInlineEnd: isRTL ? `${left * scaleX}px` : undefined,
            top: `${top * scaleY}px`,
            width: `${width * scaleX}px`,
            height: `${height * scaleY}px`,
            border: isSelected
                ? `3px solid ${theme.palette.secondary.main}`
                : `3px solid ${theme.palette.primary.main}`,
            backgroundColor: isSelected ? 'transparent' : 'rgba(0, 0, 0, 0.3)',
            cursor: 'pointer',
            boxSizing: 'border-box',
        };
    };

    const origImage = Object.values(props.eventDetails?.artifact_path || {}).find((path) =>
        path.includes('incident_image'),
    );
    let image;
    if (!image) {
        if (props.numberOfStreams === 1) {
            image = addOverlay ? SingleStreamOverlay : SingleStreamNoOverlay;
        } else if (addOverlay) {
            image = DSOverlay;
        } else {
            image = DSNoOverlay;
        }
    }
    // Quick download is only available when stream selection is disabled and no overlay/unblur
    const isQuickDownload = !enableStreamSelection && !addOverlay && (!canUnblur || !unblur);

    return (
        <Dialog id="event-video-modal" open={true} onClose={props.onClose}>
            <Box
                sx={{
                    backgroundColor: 'secondary.main',
                    top: 0,
                    p: 1,
                    zIndex: 2,
                    minHeight: 0,
                }}
            >
                <Box sx={{ position: 'absolute', display: 'flex', right: 0, top: 0, mt: 0.5, mr: 0.5 }}>
                    <IconButton
                        id="btn-close-modal"
                        data-testid="btn-close-modal"
                        size="small"
                        onClick={() => props.onClose()}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DownloadIcon sx={{ mr: 0.5 }} />
                    <Typography>{t('content.events.video.download.send_download_request')}</Typography>
                </Box>
            </Box>
            <Box sx={{ px: 2 }}>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', pt: 1 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                id="add-overlay-checkbox"
                                data-testid="add-overlay-checkbox"
                                checked={addOverlay}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddOverlay(e.target.checked)}
                                disableRipple
                            />
                        }
                        label={<Typography>{t('content.events.video.download.add_overlay')}</Typography>}
                    />
                    {canUnblur ? (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    id="unblur-checkbox"
                                    data-testid="unblur-checkbox"
                                    checked={unblur}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUnblur(e.target.checked)}
                                    disableRipple
                                />
                            }
                            label={<Typography>{t('content.events.video.download.remove_blur')}</Typography>}
                        />
                    ) : null}
                    {availableStreams.size > 1 ? (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    id="enable-stream-selection-checkbox"
                                    data-testid="enable-stream-selection-checkbox"
                                    checked={enableStreamSelection}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setEnableStreamSelection(e.target.checked)
                                    }
                                    disableRipple
                                />
                            }
                            label={
                                <Typography>{t('content.events.video.download.enable_stream_selection')}</Typography>
                            }
                        />
                    ) : null}
                </Box>
                <Box sx={{ width: '480px', display: 'flex', justifyContent: 'center', p: 1 }}>
                    {origImage ? (
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                            <img
                                ref={imageRef}
                                src={origImage}
                                alt="Download video original"
                                onLoad={handleImageLoad}
                                style={{
                                    maxHeight: '360px',
                                    maxWidth: '100%',
                                    objectFit: 'contain',
                                    display: 'block',
                                }}
                            />
                            {addOverlay ? (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        width: '75px',
                                        height: '25px',
                                        backgroundColor: '#939393',
                                    }}
                                />
                            ) : null}
                            {enableStreamSelection &&
                                Array.from(availableStreams).map((stream) => {
                                    const style = getStreamOverlayStyle(stream);
                                    if (!style) return null;
                                    const isSelected = selectedStreams.has(stream);
                                    // Extract numeric height value from style (e.g., "120px" -> 120)
                                    const heightStr = style.height as string;
                                    const streamHeight = parseFloat(heightStr.replace('px', ''));
                                    const iconSize = streamHeight * 0.2;
                                    return (
                                        <Box
                                            key={stream}
                                            data-testid={`stream-overlay-${stream}`}
                                            sx={{
                                                ...style,
                                                '&::before': !isSelected
                                                    ? {
                                                          content: '""',
                                                          position: 'absolute',
                                                          top: 0,
                                                          left: 0,
                                                          right: 0,
                                                          bottom: 0,
                                                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                          pointerEvents: 'none',
                                                      }
                                                    : {},
                                            }}
                                            onClick={() => toggleStreamSelection(stream)}
                                        >
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    insetInlineStart: isRTL ? undefined : 4,
                                                    insetInlineEnd: isRTL ? 4 : undefined,
                                                    zIndex: 1,
                                                    pointerEvents: 'none',
                                                }}
                                            >
                                                {isSelected ? (
                                                    <CheckCircleIcon
                                                        sx={{
                                                            fontSize: `${iconSize}px`,
                                                            color: theme.palette.secondary.main,
                                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                            borderRadius: '50%',
                                                        }}
                                                    />
                                                ) : (
                                                    <RadioButtonUncheckedIcon
                                                        sx={{
                                                            fontSize: `${iconSize}px`,
                                                            color: theme.palette.primary.main,
                                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                            borderRadius: '50%',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                        </Box>
                    ) : (
                        <img
                            src={image}
                            alt="Download video skeleton"
                            style={{ height: '180px', width: 'auto', objectFit: 'contain', marginTop: '10px' }}
                        />
                    )}
                </Box>
                <Box sx={{ width: '100%', py: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                        id="quick-download-btn"
                        data-testid="quick-download-btn"
                        variant="contained"
                        color="secondary"
                        disabled={!isQuickDownload || quickDownloadLoading}
                        onClick={async () => {
                            setQuickDownloadLoading(true);
                            try {
                                const response = await fetch(props.artifact, { mode: 'cors' });
                                if (!response.ok) throw new Error('Download failed');
                                const blob = await response.blob();
                                const ephemeralUrl = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = ephemeralUrl;
                                link.download = filenameForEventDownload(
                                    props.eventId,
                                    props.artifact,
                                    props.eventDetails?.timestamp,
                                    props.eventDetails?.device?.license_plate,
                                );
                                link.click();
                                URL.revokeObjectURL(ephemeralUrl);
                                props.onClose();
                            } catch {
                                props.setAlert({
                                    message: t('content.events.video.download.request_failed'),
                                    type: 'error',
                                    duration: 6000,
                                });
                            } finally {
                                setQuickDownloadLoading(false);
                            }
                        }}
                    >
                        {quickDownloadLoading
                            ? t('content.events.video.download.quick_download_loading', 'Downloading…')
                            : t('content.events.video.download.quick_download')}
                    </Button>
                    <Button
                        id="send-dowload-request-btn"
                        data-testid="send-dowload-request-btn"
                        variant="contained"
                        color="secondary"
                        disabled={isQuickDownload || (enableStreamSelection && selectedStreams.size === 0)}
                        onClick={() => {
                            // Only include selected_streams if stream selection is enabled and not all streams are selected
                            const allStreamsSelected =
                                enableStreamSelection &&
                                selectedStreams.size > 0 &&
                                selectedStreams.size === availableStreams.size;
                            const selectedStreamsArray =
                                enableStreamSelection && !allStreamsSelected ? Array.from(selectedStreams) : undefined;

                            api.apiV0EventEventIdDownloadPost({
                                eventId: props.eventId,
                                downloadVideoEventRequest: {
                                    picture_in_picture: false,
                                    is_in_cabin_primary: false,
                                    add_overlay: addOverlay,
                                    unblur,
                                    selected_streams: selectedStreamsArray,
                                },
                            })
                                .then(() => {
                                    props.setAlert({
                                        message: t('content.events.video.download.request_succeed'),
                                        type: 'success',
                                        duration: 6000,
                                    });
                                    props.onClose();
                                })
                                .catch(() => {
                                    props.setAlert({
                                        message: t('content.events.video.download.request_failed'),
                                        type: 'error',
                                        duration: 6000,
                                    });
                                });
                        }}
                    >
                        {t('content.events.video.download.send_download_request')}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default DownloadEventVideoModalComponent;
